import type { QueryRowType } from "@rocicorp/zero";
import { styled } from "goober";
import type { NormalizedName } from "../../shared/normalizeName";
import { queries } from "../../shared/queries";
import type { SpaceMode } from "../../shared/schema";
import type { Slug } from "../../shared/slug";
import { isRouteType, ROUTE_TYPES } from "../../shared/stepConfig";
import { useQuery } from "../hooks/useQuery";
import {
  ROUTE_TYPE_DESCRIPTIONS,
  ROUTE_TYPE_ICONS,
  ROUTE_TYPE_INLINE_LABELS,
  ROUTE_TYPE_LABELS,
} from "../lib/routeTypeDisplay";
import { makeUrl, useCurrentUrl } from "../lib/urls";
import type { RouteNode } from "../lib/urls/types";
import { Button } from "./Button";
import { Dropdown } from "./Dropdown";
import { EmptyState } from "./EmptyState";
import { Icon, type IconName } from "./Icon";
import { Link } from "./Link";
import { LiveIndicator } from "./LiveIndicator";
import { PageCard } from "./PageCard";
import { Text } from "./Text";

const Outer = styled("div")`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const SecondaryHeader = styled("div")`
  position: sticky;
  top: var(--sticky-top, 0px);
  z-index: 3;
  background: var(--surface-primary);
  border-bottom: 0.5px solid var(--border-alpha);
  display: flex;
  align-items: center;
  padding: 8px 24px 8px 12px;
  gap: 8px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 8px 12px;
  }
`;

const FilterPill = styled(Link)`
  all: unset;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 24px;
  color: var(--text-secondary);
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;

  &[data-selected="true"] {
    background: var(--alpha4);
    color: var(--text-primary);
  }
  &:hover {
    background: var(--alpha4);
    color: var(--text-primary);
  }
`;

const SortSpacer = styled("div")`
  margin-left: auto;
`;

const FilterDivider = styled("div")`
  display: flex;
  align-items: center;
  color: var(--border-alpha);
`;

const VIEWED_SORT_OPTION = {
  key: "viewed",
  label: "Recently viewed",
  icon: "history",
} as const;

const SORT_OPTIONS = [
  { key: "updated", label: "Recently updated", icon: "sparkles" },
  { key: "name", label: "Name", icon: "arrow-down-a-z" },
] as const;

type SortKey =
  | (typeof SORT_OPTIONS)[number]["key"]
  | typeof VIEWED_SORT_OPTION.key;

const Grid = styled("div")`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 24px;
  padding: 32px;
  flex: 1;
  min-height: 0;
  align-content: start;
  align-items: start;
`;

const EmptyFilterArea = styled("div")`
  flex: 1;
  min-height: 0;
`;

type LoadSpaceRow = QueryRowType<ReturnType<typeof queries.loadSpace>>;
type LoadSpaceWorkflow = NonNullable<LoadSpaceRow>["workflows"][number];
export type AppWorkflow = LoadSpaceWorkflow;
export type AppSpace = {
  readonly id: string;
  readonly name: NormalizedName;
  readonly slug: Slug;
  readonly mode: SpaceMode;
};
export type AppBranch = AppWorkflow["branches"][number];
export type AppRoute = NonNullable<AppBranch["headCommit"]>["routes"][number];

export type PageRow = {
  workflow: AppWorkflow;
  space: AppSpace;
  lastSeenAt?: number;
};

export function countPageCards(rows: PageRow[]): number {
  let total = 0;
  for (const r of rows) {
    for (const b of r.workflow.branches) {
      if (b.name !== "main") continue;
      total +=
        b.headCommit?.routes.filter((route) => isRouteType(route.routeType))
          .length ?? 0;
    }
  }
  return total;
}

type Props = {
  rows: PageRow[];
  linksRoute: RouteNode;
  showSpaceColumn: boolean;
  emptyTitle: string;
  emptyDescription: string;
  isComplete: boolean;
  recents?: boolean;
};

export function PagesList({
  rows,
  linksRoute,
  showSpaceColumn,
  emptyTitle,
  emptyDescription,
  isComplete,
  recents,
}: Props) {
  const [favoriteIds] = useQuery(queries.userFavoriteWorkflowIds());
  const favoriteSet = new Set(favoriteIds.map((f) => f.workflowId));
  const {
    show,
    type,
    sort: sortParam,
  } = useCurrentUrl(linksRoute) as {
    show?: string;
    type?: string;
    sort?: string;
  };
  const filter = show === "branches" ? "branches" : "live";
  const typeFilter = isRouteType(type) ? type : null;
  const sortOptions = recents
    ? [VIEWED_SORT_OPTION, ...SORT_OPTIONS]
    : SORT_OPTIONS;
  const defaultSort: SortKey = recents ? "viewed" : "updated";
  const sort: SortKey = sortOptions.some((o) => o.key === sortParam)
    ? (sortParam as SortKey)
    : defaultSort;

  const sortedRows = [...rows].sort((a, b) => {
    if (sort === "viewed") return (b.lastSeenAt ?? 0) - (a.lastSeenAt ?? 0);
    if (sort === "name") return a.workflow.name.localeCompare(b.workflow.name);
    return b.workflow.updatedAt - a.workflow.updatedAt;
  });

  if (rows.length === 0) {
    return isComplete ? (
      <EmptyState
        title={emptyTitle}
        description={emptyDescription}
        iconName="product-cases"
      />
    ) : null;
  }

  const linkCards = sortedRows.flatMap(({ workflow, space }) =>
    (workflow.branches ?? [])
      .filter((b) =>
        filter === "live" ? b.name === "main" : b.name !== "main"
      )
      .flatMap((branch) =>
        (branch.headCommit?.routes ?? [])
          .filter((r) => isRouteType(r.routeType))
          .filter((r) => !typeFilter || r.routeType === typeFilter)
          .map((route) => (
            <PageCard
              key={`${workflow.id}-${branch.id}-${route.route}`}
              workflow={workflow}
              branch={branch}
              route={route}
              space={space}
              showSpaceColumn={showSpaceColumn}
              isFavorite={favoriteSet.has(workflow.id)}
              showSelectStepAction={false}
              showOpenWorkflowAction
            />
          ))
      )
  );

  const emptyCopy: { title: string; description: string; iconName: IconName } =
    typeFilter
      ? {
          title: `No ${ROUTE_TYPE_INLINE_LABELS[typeFilter]} links`,
          description: ROUTE_TYPE_DESCRIPTIONS[typeFilter],
          iconName: ROUTE_TYPE_ICONS[typeFilter],
        }
      : {
          title: emptyTitle,
          description: emptyDescription,
          iconName: "product-cases",
        };

  return (
    <Outer>
      <SecondaryHeader>
        {(
          [
            { key: "live", label: "Live", icon: null },
            { key: "branches", label: "Branches", icon: "git-branch" },
          ] as const
        ).map((f) => (
          <FilterPill
            key={f.key}
            to={makeUrl(linksRoute, { show: f.key })}
            data-selected={filter === f.key ? "true" : "false"}
          >
            {f.icon ? <Icon name={f.icon} size={16} /> : <LiveIndicator />}
            <Text variant="text-small-md">{f.label}</Text>
          </FilterPill>
        ))}
        <FilterDivider>
          <Icon name="slash" size={24} />
        </FilterDivider>
        {ROUTE_TYPES.map((t) => (
          <FilterPill
            key={t}
            to={makeUrl(linksRoute, { type: typeFilter === t ? "all" : t })}
            data-selected={typeFilter === t ? "true" : "false"}
          >
            <Icon name={ROUTE_TYPE_ICONS[t]} size={16} />
            <Text variant="text-small-md">{ROUTE_TYPE_LABELS[t]}</Text>
          </FilterPill>
        ))}
        <SortSpacer />
        <Dropdown
          align="bottom"
          minWidth={180}
          opener={
            <Button
              variant="secondary"
              icon="arrow-down-up"
              aria-label="Sort"
            />
          }
          sections={[
            {
              items: sortOptions.map((o) => ({
                type: "link" as const,
                key: o.key,
                to: makeUrl(linksRoute, {
                  sort: o.key === defaultSort ? "" : o.key,
                }),
                left: <Icon name={o.icon} size={16} />,
                label: o.label,
                right: (
                  <Icon
                    name="check"
                    size={12}
                    color={
                      sort === o.key ? "var(--text-secondary)" : "transparent"
                    }
                  />
                ),
              })),
            },
          ]}
        />
      </SecondaryHeader>
      {linkCards.length === 0 && isComplete ? (
        <EmptyFilterArea>
          <EmptyState
            title={emptyCopy.title}
            description={emptyCopy.description}
            iconName={emptyCopy.iconName}
          />
        </EmptyFilterArea>
      ) : (
        <Grid>{linkCards}</Grid>
      )}
    </Outer>
  );
}
